import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, UserPlus, CalendarCheck, Workflow, CheckCheck, Dot } from "lucide-react";

const ICONS = { lead: UserPlus, appointment: CalendarCheck, automation: Workflow };

export default function Notifications() {
  const navigate = useNavigate();
  const { setUnread } = useAuth();
  const [items, setItems] = useState(null);

  const load = () => api.get("/notifications").then(({ data }) => { setItems(data.notifications); setUnread(data.unread); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const open = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    if (n.link) navigate(n.link); else load();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Notifications</h1>
          <p className="text-slate-500 mt-1">Stay on top of new leads and appointments.</p>
        </div>
        <Button variant="outline" onClick={markAll} data-testid="mark-all-read-btn"><CheckCheck className="h-4 w-4 mr-1" /> Mark all read</Button>
      </div>

      {items === null ? <Skeleton className="h-64 rounded-xl" /> : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200" data-testid="notifications-empty">
          <Bell className="h-12 w-12 text-slate-300 mx-auto" />
          <h3 className="mt-4 font-heading font-semibold text-lg">You're all caught up</h3>
          <p className="text-slate-500 mt-1">New leads and appointment updates will show up here.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <Card key={n.id} onClick={() => open(n)} className={`p-4 border-slate-200 flex items-start gap-3 cursor-pointer hover:bg-slate-50 ${!n.read ? "border-l-4 border-l-blue-600" : ""}`} data-testid={`notification-${n.id}`}>
                <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-blue-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-1">{n.title}{!n.read && <Dot className="h-5 w-5 text-blue-600" />}</div>
                  <div className="text-sm text-slate-600">{n.message}</div>
                  <div className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
